/**
 * Whether a booking is paid in full, by deposit, or by the customer's choice.
 *
 * This replaces the bare `booking_require_deposit` boolean, which could only say
 * "always" or "never" for an entire account. Three things now have an opinion,
 * and they are resolved in this order:
 *
 *     booking override  →  quote override  →  account setting
 *
 * The overrides let an operator take one customer out of the account's default:
 * price a trip by hand, decide that this particular customer settles in full,
 * then send them the payment link.
 *
 * NOTHING should read `booking_payment_mode` or `booking_require_deposit`
 * directly — always go through `resolvePaymentMode`, so the precedence rule
 * lives in exactly one place. This mirrors how `areCustomQuotesEnabled` guards
 * `is_quote_only`: a raw column read there means forgetting the master switch,
 * and a raw read here means forgetting the override.
 *
 * Everything here is pure.
 */

/** How much of the fare is taken at checkout. */
export type PaymentMode = 'full' | 'deposit' | 'customer_choice'

const VALID_MODES: readonly PaymentMode[] = ['full', 'deposit', 'customer_choice']

/**
 * Coerce a stored value into a mode, or null if it isn't one.
 *
 * Deliberately strict — an unrecognised string means "no opinion" and defers to
 * the next level down, rather than guessing. A typo in a settings row should
 * fall back to the account default, never silently disable deposits.
 */
export function normalizePaymentMode(value: unknown): PaymentMode | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim().toLowerCase()
  return (VALID_MODES as readonly string[]).includes(trimmed) ? (trimmed as PaymentMode) : null
}

/** The account-level configuration, as stored in `account_settings`. */
export interface AccountPaymentSettings {
  /** `booking_payment_mode` — authoritative when present. */
  paymentMode?: unknown
  /**
   * `booking_require_deposit` — the legacy boolean, still written by the Builder
   * so every existing reader keeps working. Only consulted when
   * `paymentMode` is absent, which is the case for every account configured
   * before the three-way setting shipped.
   */
  requireDeposit?: unknown
}

function modeFromLegacyBoolean(value: unknown): PaymentMode {
  const isOn = value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true')
  return isOn ? 'deposit' : 'full'
}

/**
 * Resolve the effective payment mode for one checkout.
 *
 * Overrides are three-state: a valid mode wins, `null`/absent means "inherit",
 * and an unrecognised value is treated as inherit rather than as an error — a
 * bad row must not be able to change what a customer is charged.
 */
export function resolvePaymentMode(params: {
  accountSettings: AccountPaymentSettings
  /** `booking_quotes.deposit_mode_override`. */
  quoteOverride?: unknown
  /** `bookings.deposit_mode_override`. */
  bookingOverride?: unknown
}): PaymentMode {
  const fromBooking = normalizePaymentMode(params.bookingOverride)
  if (fromBooking) return fromBooking

  const fromQuote = normalizePaymentMode(params.quoteOverride)
  if (fromQuote) return fromQuote

  const fromAccount = normalizePaymentMode(params.accountSettings.paymentMode)
  if (fromAccount) return fromAccount

  return modeFromLegacyBoolean(params.accountSettings.requireDeposit)
}

/**
 * Whether a deposit is taken, given the mode and (for `customer_choice`) what
 * the customer picked.
 *
 * `customer_choice` with no selection yet defaults to taking a deposit — the
 * smaller commitment, and what the payment page pre-selects.
 */
export function takesDeposit(mode: PaymentMode, customerSelection?: 'deposit' | 'full' | null): boolean {
  if (mode === 'full') return false
  if (mode === 'deposit') return true
  return customerSelection !== 'full'
}

/**
 * Whether the customer is allowed to pick at checkout.
 *
 * The payment step renders its deposit/full toggle from this, and the
 * payment-mode route refuses any request where this is false — otherwise the
 * toggle would be a hole straight through an operator's override.
 */
export function allowsCustomerChoice(mode: PaymentMode): boolean {
  return mode === 'customer_choice'
}
